import React, { useState, useEffect } from 'react';
import {
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  View,
  ActivityIndicator,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';

import { createPropertyName, updatePropertyName, getPlantationsData } from '../../../api/AllAddress ';
import BasicModalContainer from '../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../common/components/basicmodal/ModalFooter';
import { msgStr } from '../../../common/constants/Message';
import { useAlertModal } from '../../../common/hooks';
import { commonModalStyle } from '../../../common/components/basicmodal';
import MapModal from '../locationmanager/MapModal';

const AddPropertyModal = ({ isModalVisible, details, setUpdatePropertyNamesTrigger, closeModal }) => {
  const isUpdate = details ? true : false;

  const { showAlert } = useAlertModal();
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [plantations, setPlantations] = useState([]);
  const [isMapModalVisible, setIsMapModalVisible] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<any>({
    id: null,
    property_name : null,
    plantation_id: null,
    lat: null,
    lng: null,
  });

  useEffect(() => {
    getPlantationsData((jsonRes) => {
      setPlantations(jsonRes);
    })
  }, [])

  useEffect(() => {
    if (isModalVisible && details) {
      setFormValues({
        id: details.id,
        property_name : details.property_name,
        plantation_id: details.plantation_id,
        lat: details.lat !== null && details.lat !== undefined ? String(details.lat) : null,
        lng: details.lng !== null && details.lng !== undefined ? String(details.lng) : null,
      })
    } else {
      setFormValues({
        id: null,
        property_name : null,
        plantation_id: null,
        lat: null,
        lng: null,
      })
    }
  }, [isModalVisible]);

  const updateFormValues = (key: string, value: any) : void => {
    setFormValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const AddPropertyNameButtonHandler = () => {
    if(!checkInput()) return;

    setIsLoading(true);

    const handleResponse = (jsonRes, status) => {
      switch (status) {
        case 201:
          showAlert('success', jsonRes.message);
          setUpdatePropertyNamesTrigger(true);
          closeModal();
          break;
        case 409:
          showAlert('error', "This address already exists");
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          closeModal();
          break;
      }
      setIsLoading(false);
    };

    const payload = {
      ...formValues,
      lat: formValues.lat === null || formValues.lat === '' ? null : parseFloat(String(formValues.lat).trim()),
      lng: formValues.lng === null || formValues.lng === '' ? null : parseFloat(String(formValues.lng).trim()),
      bulkUpdateGeoLocation: true
    };

    if (isUpdate) {
      updatePropertyName(payload, (jsonRes, status) => {
        handleResponse(jsonRes, status);
      });
    } else {
      createPropertyName(payload, (jsonRes, status) => {
        handleResponse(jsonRes, status);
      });
    }
  };

  const checkInput = () => {
    let newErrors: { [key: string]: string } = {};
  
    if (!formValues.property_name || !formValues.property_name.trim()) {
      newErrors.property_name = msgStr('emptyField');
    }
  
    if (!formValues.plantation_id) {
      newErrors.plantation_id = "Please select an area";
    }

    if (formValues.lat?.toString().trim()) {
      const latNum = parseFloat(String(formValues.lat).trim());
      if (isNaN(latNum)) newErrors.lat = 'Latitude must be a number';
      else if (latNum < -90 || latNum > 90) newErrors.lat = 'Latitude must be between -90 and 90';
    }

    if (formValues.lng?.toString().trim()) {
      const lngNum = parseFloat(String(formValues.lng).trim());
      if (isNaN(lngNum)) newErrors.lng = 'Longitude must be a number';
      else if (lngNum < -180 || lngNum > 180) newErrors.lng = 'Longitude must be between -180 and 180';
    }
  
    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <Modal
      animationType="none"
      transparent={true}
      visible={isModalVisible}
    >
      <BasicModalContainer>
        <ModalHeader label={`${isUpdate ? 'Update' : 'Add'}` + ' Address'} closeModal={closeModal} />
        <ModalBody>
          <View>
            <Text style={styles.label}>Property name</Text>
            <TextInput
              style={styles.input}
              placeholder="Property name"
              value={formValues.property_name || ''}
              onChangeText={val=>updateFormValues('property_name', val)}
              placeholderTextColor="#ccc"
              onBlur={checkInput}
              onSubmitEditing={AddPropertyNameButtonHandler}
            />
            {validationErrors.property_name && (
              <Text style={styles.message}>{validationErrors.property_name}</Text>
            )}
          </View>

          <View>
            <Text style={styles.label}>Area</Text>
            <Picker
              style={styles.select}
              selectedValue={formValues.plantation_id || ''}
              onValueChange={(val, index) => {
                updateFormValues('plantation_id', val ? val : null);
              }}
            >
              <Picker.Item label={""} value={""} />
              {plantations.map(plantation => (
                <Picker.Item key={plantation.id} label={plantation.plantation} value={plantation.id} />
              ))}
            </Picker>
            {validationErrors.plantation_id && (
              <Text style={styles.message}>{validationErrors.plantation_id}</Text>
            )}
          </View>

          <View>
            <Text style={styles.label}>Latitude</Text>
            <TextInput
              style={styles.input}
              placeholder="Latitude"
              value={formValues.lat !== null && formValues.lat !== undefined ? String(formValues.lat) : ''}
              onChangeText={val=>updateFormValues('lat', val)}
              placeholderTextColor="#ccc"
              onBlur={checkInput}
              blurOnSubmit={true}
              onSubmitEditing={AddPropertyNameButtonHandler}
            />
            {validationErrors.lat && (
              <Text style={styles.message}>{validationErrors.lat}</Text>
            )}
          </View>

          <View>
            <Text style={styles.label}>Longitude</Text>
            <TextInput
              style={styles.input}
              placeholder="Longitude"
              value={formValues.lng !== null && formValues.lng !== undefined ? String(formValues.lng) : ''}
              onChangeText={val=>updateFormValues('lng', val)}
              placeholderTextColor="#ccc"
              onBlur={checkInput}
              blurOnSubmit={true}
              onSubmitEditing={AddPropertyNameButtonHandler}
            />
            {validationErrors.lng && (
              <Text style={styles.message}>{validationErrors.lng}</Text>
            )}
          </View>

          <TouchableOpacity onPress={() => setIsMapModalVisible(true)}>
            <Text style={styles.addButton}>Select on Map</Text>
          </TouchableOpacity>

        </ModalBody>
        <ModalFooter>
          <TouchableOpacity onPress={AddPropertyNameButtonHandler}>
            <Text style={styles.addButton}>{isUpdate ? 'Update' : 'Add'}</Text>
          </TouchableOpacity>
        </ModalFooter>
      </BasicModalContainer>
      {isLoading && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      )}

       <MapModal
          isVisible={isMapModalVisible}
          onClose={() => setIsMapModalVisible(false)}
          onLocationSelect={(location) => {
            updateFormValues('lat', location.latitude.toString());
            updateFormValues('lng', location.longitude.toString());
            setIsMapModalVisible(false);
          }}
          initialLatitude={formValues.lat ? parseFloat(formValues.lat) : null}
          initialLongitude={formValues.lng ? parseFloat(formValues.lng) : null}
        />
    </Modal>
  );
};

const styles = commonModalStyle;

export default AddPropertyModal;
